import { useEffect, useState } from "react";
import { readConsent, writeConsent, CONSENT_DEFAULTS } from "@/lib/consent";
import { Link } from "react-router-dom";
import { Cookie, ShieldCheck, ChevronRight } from "lucide-react";

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [prefs, setPrefs] = useState({ ...CONSENT_DEFAULTS });

  useEffect(() => {
    if (!readConsent()) setVisible(true);
    const onOpen = () => { setVisible(true); setShowDetails(true); };
    window.addEventListener("pb24-open-consent", onOpen);
    return () => window.removeEventListener("pb24-open-consent", onOpen);
  }, []);

  if (!visible) return null;

  const acceptAll = () => {
    writeConsent({ necessary: true, analytics: true, marketing: true });
    setVisible(false);
  };
  const rejectAll = () => {
    writeConsent({ necessary: true, analytics: false, marketing: false });
    setVisible(false);
  };
  const savePrefs = () => {
    writeConsent({ necessary: true, analytics: prefs.analytics, marketing: prefs.marketing });
    setVisible(false);
  };

  return (
    <div
      data-testid="cookie-banner"
      className="fixed bottom-0 left-0 right-0 z-[60] p-3 md:p-5"
      role="dialog"
      aria-live="polite"
      aria-label="Cookie-Einstellungen"
    >
      <div className="mx-auto max-w-6xl bg-ink-950/95 backdrop-blur-xl border border-gold/40 shadow-[0_25px_60px_-15px_rgba(200,164,77,0.35)]">
        {/* Main row */}
        <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6 p-6 md:p-8">
          <div className="flex items-start gap-4 flex-1">
            <span className="hidden sm:flex h-11 w-11 border border-gold/50 rounded-full items-center justify-center text-gold shrink-0">
              <Cookie className="h-5 w-5" strokeWidth={1.5} />
            </span>
            <div className="flex-1">
              <p className="eyebrow mb-2"><span className="gold-line" /> Ihre Privatsphäre</p>
              <p className="text-cream/80 text-sm leading-relaxed max-w-3xl">
                Wir verwenden Cookies für notwendige Funktionen sowie, nach Ihrer Einwilligung,
                für Statistik (PostHog mit Sitzungsaufzeichnung, Google Analytics und Metricool) und Marketing (Meta Pixel).
                Sie können Ihre Auswahl jederzeit im Footer unter <em>Cookie-Einstellungen</em> anpassen.
                Details finden Sie in unserer{" "}
                <Link to="/datenschutz" className="text-gold link-underline" data-testid="cookie-link-privacy">
                  Datenschutzerklärung
                </Link>.
              </p>
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-2.5 w-full lg:w-auto shrink-0">
            <button
              type="button"
              onClick={rejectAll}
              className="btn-ghost text-[0.72rem] px-6 py-3"
              data-testid="cookie-reject"
            >
              Nur notwendige
            </button>
            <button
              type="button"
              onClick={() => setShowDetails((v) => !v)}
              className="btn-ghost text-[0.72rem] px-6 py-3"
              data-testid="cookie-settings-toggle"
            >
              Einstellungen <ChevronRight className={`h-3.5 w-3.5 transition-transform ${showDetails ? "rotate-90" : ""}`} />
            </button>
            <button
              type="button"
              onClick={acceptAll}
              className="btn-gold text-[0.72rem] px-6 py-3 whitespace-nowrap"
              data-testid="cookie-accept-all"
            >
              Alle akzeptieren
            </button>
          </div>
        </div>

        {/* Details */}
        {showDetails && (
          <div className="border-t border-cream/[0.08] p-6 md:p-8 space-y-4" data-testid="cookie-details">
            <ConsentRow
              icon={ShieldCheck}
              title="Notwendige Cookies"
              desc="Sitzungs- und Warenkorbdaten, Login, Sprachauswahl. Diese können nicht deaktiviert werden."
              checked
              disabled
            />
            <ConsentRow
              title="Statistik (PostHog, Google Analytics 4 & Metricool)"
              desc="Nutzungsstatistik und Sitzungsaufzeichnung (Eingaben unkenntlich, Server von PostHog in der EU) zur Verbesserung der Website; wird nur nach Ihrer Einwilligung geladen."
              checked={prefs.analytics}
              onChange={(v) => setPrefs({ ...prefs, analytics: v })}
              testId="consent-analytics"
            />
            <ConsentRow
              title="Marketing (Meta Pixel)"
              desc="Personalisierte Werbung auf Facebook und Instagram sowie Conversion-Tracking."
              checked={prefs.marketing}
              onChange={(v) => setPrefs({ ...prefs, marketing: v })}
              testId="consent-marketing"
            />

            <div className="flex justify-end gap-2 pt-3 border-t border-cream/[0.06]">
              <button
                type="button"
                onClick={savePrefs}
                className="btn-gold text-[0.72rem] px-6 py-3"
                data-testid="cookie-save-preferences"
              >
                Auswahl speichern
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ConsentRow({ icon: Icon, title, desc, checked, onChange, disabled, testId }) {
  return (
    <label className="flex items-start gap-4 py-2 cursor-pointer">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => !disabled && onChange?.(!checked)}
        disabled={disabled}
        data-testid={testId}
        className={`shrink-0 mt-1 relative h-6 w-11 rounded-full transition-colors ${
          checked ? "bg-gold" : "bg-cream/20"
        } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
      >
        <span
          className={`absolute top-0.5 h-5 w-5 rounded-full bg-ink-950 transition-transform ${
            checked ? "translate-x-5" : "translate-x-0.5"
          }`}
        />
      </button>
      <div className="flex-1">
        <p className="text-cream text-sm font-medium flex items-center gap-2">
          {Icon && <Icon className="h-3.5 w-3.5 text-gold" />}
          {title}
        </p>
        <p className="text-cream/60 text-xs mt-1 leading-relaxed">{desc}</p>
      </div>
    </label>
  );
}
